import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { Col, Row, Container } from "react-bootstrap";
import { CircularProgress } from '@mui/material';
import PropTypes from 'prop-types';
import { format } from 'date-fns';
import { SESSION_STORAGE_ACCOUNT_KEY, SESSION_STORAGE_ESIGN_AUTH_KEY, getSessionStorageItem } from '../../../../sessionStorage';
import ApplicationSummary from './Status/ApplicationSummary';
import StatusStepCard from './Status/StatusStepCard';
import SendStatusEmailButton from '../../Common/Status/SendStatusEmailButton';
import CtaSolution from '../../Common/CtaSolution/CtaSolution';
import './Status.scss';

export default function Status(props) {

  const navigate = useNavigate();
  const covrServicesUrl = window.__ENV__.REACT_APP_COVR_SERVICES_URL;

  const accountObj = getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY);
  const promotedProducts = JSON.parse(accountObj.account.promotedProducts);
  const promotedProductCount = promotedProducts ? promotedProducts.filter(p => p.isEnabled).length : 0;

  const esignAuthJSON = getSessionStorageItem(SESSION_STORAGE_ESIGN_AUTH_KEY);

  const WAIT_SECONDS = 10;

  const passcode = (esignAuthJSON && esignAuthJSON.passcode) || (props.formState.steps.agreementToUnderwriting.socialSecurityNumber && props.formState.steps.agreementToUnderwriting.socialSecurityNumber.replace(/-/g, '').substring(5));
  const birthdate = (esignAuthJSON && esignAuthJSON.birthdate) || props.formState.steps.basicInfo.birthdate;
  const applicationID = (esignAuthJSON && esignAuthJSON.applicationID) || props.formState.appIdentifier;

  const [approved, setApproved] = useState(false);
  const [attempts, setAttempts] = useState(1);
  const [loading, setLoading] = useState(true);
  const [statusResponse, setStatusResponse] = useState(null);

  useEffect(() => {
    fetch(`${covrServicesUrl}/api/AppStatus/GetAppStatus?passcode=${passcode}&birthdate=${birthdate}&applicationID=${applicationID}`,
      {
        method: 'GET',
        headers: {
          'SessionId': getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY).sessionId
        }
      })
      .then(res => res.json())
      .then(json => {
        setStatusResponse(json)
        setLoading(false)
      });
  }, [])

  const waiting = () => {
    setTimeout(() => setAttempts(attempts + 1), 1000 * WAIT_SECONDS);
  }

  const checkFinalSubmissionStatus = () => {
    fetch(`${covrServicesUrl}/api/Americo/CheckSubmissionStatus?PolicyNumber=${(statusResponse && statusResponse.PolicyNumber) || props.formState.policyNumber}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'SessionId': getSessionStorageItem(SESSION_STORAGE_ACCOUNT_KEY).sessionId,
      },
    })
      .then(x => x.json())
      .then(json => {
        if (json.hasSubmitted) {
          setApproved(true);
        }
        else {
          waiting();
        }
      });
  }

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    });
  };

  const handlePromotedProduct = (ctaType, filter, redirectUri) => {
    if (ctaType === "redirect" && redirectUri) {
      navigate(redirectUri);
      scrollToTop();
    }
  }

  useEffect(() => {
    if (!approved) {
      checkFinalSubmissionStatus();
    }
  }, [attempts])

  if (loading) {
    return <div className='d-flex justify-content-center'><CircularProgress /></div>;
  }

  return (
    <Container className="status loan-protection">
      <Row>
        <Col lg={4} className='w-100'>
          <ApplicationSummary firstName={statusResponse.InsuredName}
            lastName=""
            coverageAmount={statusResponse.Coverage}
            premium={statusResponse.Premium}
            term={statusResponse.Term}
            emailAddress={statusResponse.EmailAddress}
            passcode={passcode}
            birthdate={birthdate}
            applicationID={applicationID}
            includeFaq={false}
          />
        </Col>
        <Col lg={8} className='w-100'>
          <p className="statusTitle">Nice job - now it's our turn</p>

          <p className="statusSubTitle">You’ve completed your application. <br />There’s just two more steps on our end.</p>

          <Row className="statusInner">
            <StatusStepCard active={!approved} stepNumber={1} stepTitle={'Automated Review'} stepStatusLabel={!approved ? '5 mins' : 'Complete'} showSpinner={!approved} showCheckmark={approved}>
                <AutomatedReview birthdate={birthdate} applicationID={applicationID} passcode={passcode} clientUserID={statusResponse.ClientUserID} />
            </StatusStepCard>

            <StatusStepCard active={approved} stepNumber={2} stepTitle={'Application Decision'}>
                <Approved policyNumber={statusResponse.PolicyNumber || props.formState.policyNumber} policyEffectiveDate={statusResponse.PolicyDate ? format(new Date(statusResponse.PolicyDate), 'MM/dd/yyyy') : props.formState.policyEffectiveDate} />
            </StatusStepCard>
          </Row>
        </Col>
      </Row>
      <Row className="promotedProducts">
        <Col>
          { promotedProducts && (promotedProductCount > 0) && <CtaSolution data={promotedProducts} productType={props.productType} handlePromotedProduct={handlePromotedProduct}/> }
        </Col>
      </Row>
    </Container>
  )
}

function Approved(props) {

  return (
    <React.Fragment>
      <span className='stepTextHeader'>Congratulations your application has been approved!</span><br /><br />

      <div className='policyDetails'>
        <div className='policyDetail'>
          <span className='policyDetailName'>Policy Number</span>
          <span className={`policyDetailValue primary-color`}>{props.policyNumber}</span>
        </div>
        <div className='policyDetail'>
          <span className='policyDetailName'>Policy Effective Date</span>
          <span className={`policyDetailValue primary-color`}>{props.policyEffectiveDate}</span>
        </div>
      </div>

      Your policy will be delivered soon, but don't worry - your coverage is in effect now. Purchasing life insurance is an important step toward protecting your family's future and securing peace of mind. If you have any questions, don't hesitate to reach out to our team.<br /><br />

      <span className='stepTextHeader'>What's next?</span><br /><br />

      Look for an email from Covr containing important information about your policy. Your new policy will be mailed to you directly from Americo. In the case of a collateral assignment of your policy, please review your email for your certificate of insurance from Americo.
    </React.Fragment>
  )
}

Approved.propTypes = {
  policyNumber: PropTypes.string.isRequired,
}

function AutomatedReview(props) {
  return (
    <React.Fragment>
      Your policy application is being reviewed and a decision will be returned shortly. You will receive an email when a decision is returned - usually within 5 minutes.<br /><br />

      <SendStatusEmailButton passcode={props.passcode} birthdate={props.birthdate} applicationID={props.applicationID} clientUserID={props.clientUserID} />
    </React.Fragment>
  )
}

AutomatedReview.propTypes = {
  passcode: PropTypes.string.isRequired,
  birthdate: PropTypes.string.isRequired,
  applicationID: PropTypes.string.isRequired,
  clientUserID: PropTypes.string.isRequired,
}
